// PrecoComDesconto.js — o preço de um e-book, com o "de/por" desenhado igual
// em todo lugar que mostra livro.
//
// ================= POR QUE UM COMPONENTE =================
//
// Todo livro da vitrine de destaques tem desconto no e-book, e o mesmo livro
// aparece em outras vitrines: "Você poderia gostar" e o Especial da Casa, na
// própria home; a espiada rápida do catálogo; o "Compre junto" da página do
// produto. Cada uma desenhava o preço à mão, e as quatro liam `livro.preco` —
// o CHEIO — enquanto mandavam para o carrinho o preço com desconto. O mesmo
// livro saía a R$ 27,00 num card e a R$ 30,00 no card de baixo. Quem vê dois
// preços para o mesmo livro não conclui que um é promoção: conclui que o site
// erra.
//
// O número vem de src/utils/precoLivro.js, que lê o que o servidor calculou.
// Este arquivo só desenha — ele nunca decide se há desconto.
//
// ================= O DESENHO =================
//
//     R$ 30,00              o preço antigo, riscado, EM CIMA e menor
//     R$ 27,00  [10% OFF]   o que se cobra, com o selo encostado à direita
//
// É o "de/por" do varejo: lê-se de cima para baixo, do preço velho para o
// novo. Pedido do cliente em 10/09/2026. Antes o riscado vinha na mesma linha,
// DEPOIS do número, e o selo, sem espaço ao lado, caía sozinho para baixo.
//
// A LINHA DO RISCADO É RESERVADA mesmo sem desconto (`reservarRiscado`). Numa
// grade, um card em promoção e o vizinho sem ela ficariam com o preço em
// alturas diferentes — a linha torta que o resto do card já evita com alturas
// fixas. Onde não há vizinho para alinhar, quem usa desliga.
//
// ================= O SELO =================
//
// CHAPADO, com texto ESCURO (`text-ink-fixed`): 4,88:1 nos dois temas. Branco
// sobre este verde dava 2,2:1 no tema escuro, que é o padrão do site.
//
// Sem `border`: o bootstrap.min.css declara `.border` com `!important` (ver
// App.css) e o contorno sairia na cor da hairline do tema, não em verde.
//
// `[text-shadow:none]` porque o Especial da Casa põe o preço sobre a capa com
// sombra de texto, e sombra escura em letra escura vira borrão.
import React from "react";
import {
  fmtPreco,
  percentualOff,
  precoCheio,
  precoDeVenda,
  temDesconto,
} from "../../utils/precoLivro";

export const SeloDesconto = ({ pct, className = "" }) => (
  <span
    className={
      "inline-flex shrink-0 items-center gap-1 rounded-selo bg-insigne-promo px-1.5 py-0.5 " +
      "text-[11px] font-extrabold uppercase tracking-wide text-ink-fixed [text-shadow:none] " +
      className
    }
  >
    <i className="bi bi-tag-fill text-[10px]" aria-hidden="true" />
    {pct}% OFF
  </span>
);

const PrecoComDesconto = ({
  livro,
  /** Classes do número grande. */
  numero = "text-xl md:text-2xl",
  /** Cor do preço antigo. */
  riscado = "text-white/45",
  /** Guarda a altura da linha do riscado mesmo sem desconto (grades). */
  reservarRiscado = true,
  /** O selo pode descer de linha quando não cabe ao lado do número? Em card
      de altura fixa não pode: ele vazaria por baixo. */
  quebrar = true,
  /** Classes extras da linha do preço (ex.: altura fixa). */
  linha = "",
  /** Nome do formato ("E-book"), na linha do riscado. Só quando o livro tem
      mais de um formato: sozinho, dizer "E-book" é ruído. */
  rotulo = "",
  /** O selo "10% OFF" ao lado do número.
      `false` em quem mostra o desconto em OUTRO lugar — o card de livro leva a
      tarja inclinada na quina desde 11/09/2026 (ver CardDestaque), e o selo
      aqui seria o mesmo aviso duas vezes no mesmo card. */
  mostrarSelo = true,
  className = "",
}) => {
  const venda = precoDeVenda(livro);
  if (venda == null) return null;
  const desconto = temDesconto(livro);

  return (
    <div className={className}>
      {(desconto || reservarRiscado || rotulo) && (
        <p
          className={"flex h-4 items-baseline gap-1.5 text-sm leading-4 " + riscado}
          aria-hidden={desconto || rotulo ? undefined : true}
        >
          {/* O rótulo mora na linha do riscado, que já era espaço reservado:
              assim o card com dois formatos não fica mais alto que o vizinho. */}
          {rotulo && (
            <span className="text-[10px] font-bold uppercase tracking-wide text-white/55">
              {rotulo}
            </span>
          )}
          {desconto && (
            <>
              <span className="sr-only">De </span>
              <span className="line-through">R$ {fmtPreco(precoCheio(livro))}</span>
            </>
          )}
        </p>
      )}
      <p
        className={
          "flex items-center gap-x-1.5 gap-y-1 " +
          (quebrar ? "flex-wrap " : "flex-nowrap ") +
          linha
        }
      >
        <span className="shrink-0 whitespace-nowrap">
          {desconto && <span className="sr-only">por </span>}
          <span className="text-xs align-top">R$</span>{" "}
          <span className={numero + " font-bold"}>{fmtPreco(venda)}</span>
        </span>
        {desconto && mostrarSelo && <SeloDesconto pct={percentualOff(livro)} />}
      </p>
    </div>
  );
};

export default PrecoComDesconto;
